/**
 * @file DataTable.tsx
 * @description Enhanced industrial-grade TanStack Table v8 wrapper.
 *
 * Features:
 *  - Sticky header support
 *  - Integrated toolbar slot (FilterBar + Actions)
 *  - Page size selector
 *  - Optional Row numbers (Serial No.)
 *  - Striped / Dense / Card-less modes
 *  - Sorting, Column visibility, Row selection
 *  - Responsive overflow handling
 */

import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Settings2,
  Hash,
} from 'lucide-react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { EmptyState } from '@/components/common/EmptyState';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface PaginatedMeta {
  totalRecords: number;
  totalPages: number;
}

interface DataTableProps<TData extends object, TValue = unknown> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;

  /** Called when a row is clicked */
  onRowClick?: (row: TData) => void;

  // ── Pagination ──
  page?: number;
  totalPages?: number;
  totalRecords?: number;
  limit?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  pageSizeOptions?: number[];

  /** Internal client-side pagination */
  useInternalPagination?: boolean;

  // ── Filters & Toolbar ──
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  globalFilterFn?: import('@tanstack/react-table').FilterFnOption<TData>;
  /** Custom toolbar element (typically FilterBar) */
  toolbar?: React.ReactNode;

  // ── Visual Options ──
  emptyMessage?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  showColumnVisibility?: boolean;
  /** Prepend a '#' column with row numbers */
  showRowNumbers?: boolean;
  /** Alternating row backgrounds */
  striped?: boolean;
  /** Compact row padding */
  dense?: boolean;
  /** Fix header to top of container */
  stickyHeader?: boolean;
  /** Remove card styling (border, shadow, bg) */
  noCard?: boolean;

  // ── Row Selection ──
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: React.Dispatch<React.SetStateAction<RowSelectionState>>;

  className?: string;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const SkeletonRow = React.memo(function SkeletonRow({
  colCount,
  dense,
}: {
  colCount: number;
  dense?: boolean;
}) {
  return (
    <TableRow className="hover:bg-transparent border-b-border/40">
      {Array.from({ length: colCount }).map((_, i) => (
        <TableCell key={i} className={cn(dense ? 'py-2' : 'py-3 h-14')}>
          <Skeleton className={cn('h-4 rounded', i === 0 ? 'w-3/4' : 'w-1/2')} />
        </TableCell>
      ))}
    </TableRow>
  );
});

const SortIcon = ({ direction }: { direction: 'asc' | 'desc' | false }) => {
  if (direction === 'asc') return <ChevronUp className="ml-1 h-3.5 w-3.5 shrink-0" />;
  if (direction === 'desc') return <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0" />;
  return null;
};

// ─── DataTable ────────────────────────────────────────────────────────────────

export function DataTable<TData extends object, TValue = unknown>({
  columns: userColumns,
  data,
  isLoading = false,
  onRowClick,
  page,
  totalPages,
  totalRecords,
  limit,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [10, 25, 50, 100],
  useInternalPagination = false,
  globalFilter,
  onGlobalFilterChange,
  globalFilterFn,
  toolbar,
  emptyMessage,
  emptyDescription,
  emptyAction,
  showColumnVisibility = true,
  showRowNumbers = false,
  striped = false,
  dense = false,
  stickyHeader = true,
  noCard = false,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});

  const effectiveRowSelection = rowSelection ?? internalRowSelection;
  const effectiveSetRowSelection = onRowSelectionChange ?? setInternalRowSelection;

  // ── Column Augmentation ──
  const columns = React.useMemo(() => {
    const cols = [...userColumns];
    if (showRowNumbers) {
      cols.unshift({
        id: 'rowNumber',
        header: () => (
          <div className="flex justify-center">
            <Hash className="h-3.5 w-3.5" />
          </div>
        ),
        cell: ({ row, table }) => {
          const { pageIndex, pageSize } = table.getState().pagination;
          return (
            <div className="text-center font-mono text-xs text-muted-foreground">
              {pageIndex * pageSize + row.index + 1}
            </div>
          );
        },
        size: 50,
        enableSorting: false,
        enableHiding: false,
      });
    }
    return cols;
  }, [userColumns, showRowNumbers]);

  const table = useReactTable<TData>({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection: effectiveRowSelection,
      ...(globalFilter !== undefined ? { globalFilter } : {}),
    },
    enableRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: effectiveSetRowSelection,
    ...(onGlobalFilterChange ? { onGlobalFilterChange } : {}),
    ...(globalFilterFn ? { globalFilterFn } : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(useInternalPagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    manualPagination: !useInternalPagination && page !== undefined,
    pageCount: !useInternalPagination && totalPages !== undefined ? totalPages : undefined,
  });

  // ── Pagination Logic ──
  const isExternalPagination = page !== undefined && totalPages !== undefined && !!onPageChange;
  const isInternalPagination = useInternalPagination && table.getPageCount() > 1;
  const showPagination =
    (isExternalPagination && totalPages! > 1) || isInternalPagination || !!onLimitChange;

  const currentPage = isExternalPagination ? page! : table.getState().pagination.pageIndex + 1;
  const maxPages = isExternalPagination ? totalPages! : table.getPageCount();

  const handlePrev = () => {
    if (isExternalPagination) onPageChange!(Math.max(1, page! - 1));
    else table.previousPage();
  };

  const handleNext = () => {
    if (isExternalPagination) onPageChange!(Math.min(totalPages!, page! + 1));
    else table.nextPage();
  };

  const canGoPrev = isExternalPagination ? page! > 1 : table.getCanPreviousPage();
  const canGoNext = isExternalPagination ? page! < totalPages! : table.getCanNextPage();

  const entryLabel = React.useMemo(() => {
    if (totalRecords !== undefined && limit !== undefined) {
      const from = (currentPage - 1) * limit + 1;
      const to = Math.min(currentPage * limit, totalRecords);
      return `Showing ${from}–${to} of ${totalRecords}`;
    }
    if (useInternalPagination) {
      const { pageIndex, pageSize } = table.getState().pagination;
      const from = pageIndex * pageSize + 1;
      const to = Math.min((pageIndex + 1) * pageSize, data.length);
      return `Showing ${from}–${to} of ${data.length}`;
    }
    return null;
  }, [currentPage, limit, totalRecords, data.length, table, useInternalPagination]);

  const skeletonRows = limit ?? 8;

  return (
    <div className={cn('group/table space-y-4', className)}>
      {/* Toolbar Layer */}
      {(toolbar || showColumnVisibility) && (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between px-1">
          <div className="flex-1 w-full">{toolbar}</div>

          {showColumnVisibility && (
            <div className="flex justify-end order-first lg:order-last">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 gap-2 text-xs font-medium w-full sm:w-auto"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Visible Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {table
                    .getAllColumns()
                    .filter((col) => col.getCanHide())
                    .map((col) => (
                      <DropdownMenuCheckboxItem
                        key={col.id}
                        className="capitalize"
                        checked={col.getIsVisible()}
                        onCheckedChange={(val) => col.toggleVisibility(!!val)}
                      >
                        {typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      )}

      {/* Table Container */}
      <div
        className={cn(
          'relative overflow-hidden transition-all',
          !noCard && 'rounded-xl border border-border/60 bg-card shadow-sm',
        )}
      >
        <div className={cn('overflow-x-auto', stickyHeader && 'max-h-[70vh]')}>
          <Table className="border-collapse">
            <TableHeader
              className={cn(
                'bg-muted/40',
                stickyHeader &&
                  'sticky top-0 z-20 shadow-[0_1px_0_0_rgba(0,0,0,0.05)] backdrop-blur-sm',
              )}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow
                  key={headerGroup.id}
                  className="hover:bg-transparent border-b border-border/60"
                >
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortDir = header.column.getIsSorted();
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          'font-semibold text-[11px] uppercase tracking-wider text-muted-foreground select-none',
                          canSort && 'cursor-pointer hover:text-foreground transition-colors',
                          stickyHeader && 'bg-muted/40', // Match header bg for sticky
                        )}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                      >
                        {header.isPlaceholder ? null : (
                          <div className="flex items-center gap-1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && <SortIcon direction={sortDir} />}
                          </div>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody key={currentPage} className="animate-fade-in">
              {isLoading ? (
                Array.from({ length: skeletonRows }).map((_, i) => (
                  <SkeletonRow key={i} colCount={columns.length} dense={dense} />
                ))
              ) : table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, idx) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() ? 'selected' : undefined}
                    onClick={() => onRowClick && onRowClick(row.original)}
                    className={cn(
                      'border-b border-border/40 transition-colors',
                      onRowClick && 'cursor-pointer',
                      row.getIsSelected() && 'bg-primary/5',
                      striped && idx % 2 === 1 && 'bg-muted/10',
                      'hover:bg-muted/30',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(dense ? 'py-2 px-4 text-xs' : 'py-3.5 px-4')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columns.length} className="p-0">
                    <EmptyState
                      message={emptyMessage ?? 'No records found.'}
                      description={emptyDescription}
                      action={emptyAction}
                      className="py-12"
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination Footer */}
      {showPagination && !isLoading && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-1 py-1">
          <div className="flex flex-wrap items-center gap-4 justify-between sm:justify-start">
            <p className="text-[11px] sm:text-xs text-muted-foreground font-medium order-2 sm:order-1">
              {entryLabel ?? `Page ${currentPage} of ${maxPages}`}
            </p>

            {(onLimitChange || useInternalPagination) && (
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <span className="text-[10px] sm:text-[11px] text-muted-foreground uppercase tracking-wider font-bold">
                  Rows
                </span>
                <Select
                  value={String(limit ?? table.getState().pagination.pageSize)}
                  onValueChange={(v) => {
                    if (onLimitChange) onLimitChange(Number(v));
                    else table.setPageSize(Number(v));
                  }}
                >
                  <SelectTrigger className="h-7 sm:h-8 w-[60px] sm:w-[70px] text-[10px] sm:text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pageSizeOptions.map((opt) => (
                      <SelectItem key={opt} value={String(opt)} className="text-xs">
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-1.5 sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rounded-lg transition-all hover:bg-muted"
              onClick={handlePrev}
              disabled={!canGoPrev}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="sr-only">Previous</span>
            </Button>

            <div className="flex items-center gap-1.5">
              {Array.from({ length: Math.min(maxPages, 3) }, (_, i) => {
                const pageNum =
                  maxPages <= 3
                    ? i + 1
                    : currentPage <= 2
                      ? i + 1
                      : currentPage >= maxPages - 1
                        ? maxPages - 2 + i
                        : currentPage - 1 + i;

                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === currentPage ? 'default' : 'outline'}
                    size="sm"
                    className={cn(
                      'h-8 w-8 p-0 rounded-lg text-xs transition-all',
                      pageNum === currentPage ? 'shadow-sm' : 'hover:bg-muted',
                    )}
                    onClick={() => {
                      if (isExternalPagination) onPageChange!(pageNum);
                      else table.setPageIndex(pageNum - 1);
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0 rounded-lg transition-all hover:bg-muted"
              onClick={handleNext}
              disabled={!canGoNext}
            >
              <ChevronRight className="h-4 w-4" />
              <span className="sr-only">Next</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
